import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isDemoUser, simulateDemoAction } from "@/lib/demoMode";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StudentProgressCard } from "@/components/StudentProgressCard";
import { toast } from "sonner";
import { DailyBriefCard } from "@/components/DailyBriefCard";
import { InsightsRecommendationsFeed } from "@/components/InsightsRecommendationsFeed";
import { ByTheWayNudge } from "@/components/ByTheWayNudge";
import { QuickActions } from "@/components/parent-dashboard/QuickActions";
import { UpcomingLessonsCard } from "@/components/parent-dashboard/UpcomingLessonsCard";
import { FormsToCompleteCard } from "@/components/parent-dashboard/FormsToCompleteCard";
import { PaymentsCard } from "@/components/parent-dashboard/PaymentsCard";
import { RescheduleRequestDialog } from "@/components/parent-dashboard/RescheduleRequestDialog";
import { ChooseLessonRescheduleDialog } from "@/components/parent-dashboard/ChooseLessonRescheduleDialog";
import { NewLessonRequestDialog } from "@/components/parent-dashboard/NewLessonRequestDialog";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedForm, setReschedForm] = useState({ reason: "", preferred_time: "" });
  const [chooseLessonOpen, setChooseLessonOpen] = useState(false);
  const [newLessonOpen, setNewLessonOpen] = useState(false);
  const [newLessonForm, setNewLessonForm] = useState({ student_id: "", reason: "", preferred_time: "" });
  const [newLessonSubmitting, setNewLessonSubmitting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/parent");
      setData(res.data);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitReschedule = async () => {
    if (isDemoUser(user)) {
      simulateDemoAction("this reschedule request would be sent to the office for review, and you'd get a notification once it's resolved.");
      setReschedTarget(null);
      setReschedForm({ reason: "", preferred_time: "" });
      return;
    }
    try {
      await api.post("/reschedule-requests", { appointment_id: reschedTarget.id, ...reschedForm });
      toast.success("Reschedule request sent to the office");
      setReschedTarget(null);
      setReschedForm({ reason: "", preferred_time: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    }
  };

  // v1.2 Part 1: "Request Reschedule" quick action — reuses the same
  // per-lesson reschedule dialog above, but first figures out *which*
  // upcoming lesson to target (skip the picker entirely if there's only
  // one, since that's the common case).
  const handleQuickReschedule = () => {
    const upcoming = data?.upcoming || [];
    if (upcoming.length === 0) {
      toast.info("You have no upcoming lessons to reschedule right now. Use \"Schedule Driving Lesson\" to request a new one.");
      return;
    }
    if (upcoming.length === 1) {
      setReschedTarget(upcoming[0]);
      return;
    }
    setChooseLessonOpen(true);
  };

  const pickLessonToReschedule = (appt) => {
    setChooseLessonOpen(false);
    setReschedTarget(appt);
  };

  // v1.2 Part 1: "Schedule Driving Lesson" quick action — a lightweight
  // request the office reviews and books, reusing the same
  // reschedule_requests collection/workflow staff already monitor
  // (see routes_scheduling.py request_reschedule for the new_lesson type).
  const openNewLessonDialog = () => {
    setNewLessonForm({ student_id: data?.students?.[0]?.id || "", reason: "", preferred_time: "" });
    setNewLessonOpen(true);
  };

  const submitNewLesson = async () => {
    if (isDemoUser(user)) {
      simulateDemoAction("this lesson request would be sent to the office to schedule, and you'd get a notification once it's confirmed.");
      setNewLessonOpen(false);
      return;
    }
    if (!newLessonForm.student_id) {
      toast.error("Please select a student");
      return;
    }
    setNewLessonSubmitting(true);
    try {
      await api.post("/reschedule-requests", {
        student_id: newLessonForm.student_id,
        reason: newLessonForm.reason,
        preferred_time: newLessonForm.preferred_time,
      });
      toast.success("Lesson request sent to the office — you'll be notified once it's scheduled");
      setNewLessonOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    } finally {
      setNewLessonSubmitting(false);
    }
  };

  const handleFormsAction = () => {
    if ((data?.pending_forms || []).length === 0) {
      toast.success("You're all caught up! No forms are currently required.");
      return;
    }
    navigate("/forms");
  };

  const pendingFormsCount = data?.pending_forms?.length || 0;
  const balanceTotal = (data?.balances || []).reduce((sum, b) => sum + (b.balance || 0), 0);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 rounded-[var(--radius)]" />
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      </div>
    );
  }

  if (data.students?.length === 0) {
    return (
      <div className="rounded-[var(--radius)] border bg-card p-10 text-center space-y-4" data-testid="parent-no-student-state">
        <p className="text-lg font-semibold font-heading">Welcome to DriveDesk Hub!</p>
        <p className="text-sm text-muted-foreground">Add your student to get started with scheduling lessons.</p>
        <Button onClick={() => navigate("/students")} data-testid="parent-add-student-button">
          Add a Student
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard-page">
      <DailyBriefCard brief={data.daily_brief} />

      <ByTheWayNudge nudge={data.by_the_way} />

      {/* v1.2 Part 1: Student Progress Card(s) — one per enrolled student */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="student-progress-cards-grid">
        {data.students.map((s) => (
          <StudentProgressCard key={s.id} student={s} />
        ))}
      </div>

      {/* v1.2 Part 1: Parent Quick Actions — directly beneath progress */}
      <QuickActions
        pendingFormsCount={pendingFormsCount}
        balanceTotal={balanceTotal}
        onSchedule={openNewLessonDialog}
        onReschedule={handleQuickReschedule}
        onPayment={() => navigate("/payments")}
        onForms={handleFormsAction}
      />

      <InsightsRecommendationsFeed items={data.insights} />

      <div className="grid sm:grid-cols-2 gap-4">
        {data.balances.map((b) => (
          <div key={b.student_id} className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 flex items-center justify-between" data-testid="parent-student-balance-card">
            <div>
              <p className="text-sm text-muted-foreground">Balance for</p>
              <p className="font-semibold">{b.student_name}</p>
            </div>
            <p className={`text-xl font-semibold tabular-nums font-heading ${b.balance > 0 ? "text-destructive" : "text-emerald-600"}`}>
              ${b.balance.toFixed(2)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <UpcomingLessonsCard upcoming={data.upcoming} onRequestReschedule={setReschedTarget} />

        <div className="space-y-6">
          <FormsToCompleteCard pendingForms={data.pending_forms} onOpenForm={(f) => navigate(`/forms/${f.id}/fill`)} />
          <PaymentsCard balanceTotal={balanceTotal} onViewPayments={() => navigate("/payments")} onBrowseClassrooms={() => navigate("/classrooms")} />
        </div>
      </div>

      <RescheduleRequestDialog
        open={!!reschedTarget}
        target={reschedTarget}
        form={reschedForm}
        onFormChange={setReschedForm}
        onOpenChange={(v) => !v && setReschedTarget(null)}
        onSubmit={submitReschedule}
      />

      <ChooseLessonRescheduleDialog
        open={chooseLessonOpen}
        onOpenChange={setChooseLessonOpen}
        lessons={(data.upcoming || []).filter((a) => a.type === "lesson")}
        onPick={pickLessonToReschedule}
      />

      <NewLessonRequestDialog
        open={newLessonOpen}
        onOpenChange={(v) => { setNewLessonOpen(v); }}
        students={data.students}
        form={newLessonForm}
        onFormChange={setNewLessonForm}
        submitting={newLessonSubmitting}
        onSubmit={submitNewLesson}
      />
    </div>
  );
}
